/**
 * @fileoverview The photo help (the AI allowance, D23) as a query: the share used and the
 * day some comes back, for the profile's bar (the gardener's own) and the
 * screens' quiet note (the garden in use's).
 *
 * Read again after each use of the AI (refreshAiAllowance), so the note can
 * appear on the screen where the allowance was used.
 *
 * Where: PhotoHelpBar ('mine') and AiNearNote ('garden'); refreshAiAllowance
 * is called by the photo tray, the packet scan and the check-up after each
 * AI use.
 * API: GET /ai-allowance and GET /ai-allowance/garden, with ?lang=&tz=.
 * Watch out: the language is part of the key, so switching language reads
 * it again (the "comes back" day is written in that language).
 *
 * @module useAiAllowance
 */

import { useQuery, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import { AI_ALLOWANCE_QUERY_KEY, getAiAllowance, type AiAllowance, type AiAllowanceScope } from '@/shared/api/aiAllowance';
import { useAuth } from '@/shared/hooks/useAuth';
import { useTranslation } from '@/shared/hooks/useTranslation';

/**
 * Where the gardener stands with their photo help; null for one with no AI.
 *
 * @param scope - Their own ('mine', the profile) or the garden in use's ('garden', the screens).
 * @param enabled - False to not ask (a screen whose AI is not offered).
 * @returns The query.
 */
export function useAiAllowance(scope: AiAllowanceScope, enabled = true): UseQueryResult<AiAllowance | null, Error> {
  const { mysqlUser } = useAuth();
  const { i18n: { language } } = useTranslation();
  return useQuery({
    queryKey: [...AI_ALLOWANCE_QUERY_KEY, scope, language],
    queryFn: () => getAiAllowance(scope, language),
    enabled: enabled && Boolean(mysqlUser),
  });
}

/**
 * Reads the photo help again, after a use of the AI.
 *
 * @param queryClient - The app's query client.
 */
export function refreshAiAllowance(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: AI_ALLOWANCE_QUERY_KEY });
}
